import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
    caseInsensitiveComparator,
    fromControl,
    includesIgnoreCase,
    numericLocaleComparator,
    TranslatePipe,
} from '@brunata-angular/util';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {
    SelectionTableColumn,
    SelectionTableComponent,
} from '../../shared/ui/selection-table/selection-table.component';
import {SelectionTableFilterDirective} from '../../shared/ui/selection-table/selection-table-templates.directive';

export type SystemRow = {
    systemNo: string;
    buildingName: string | null;
    address: string;
};

type SelectableSystemRow = SystemRow & {
    id: string;
    selected: boolean;
};

@Component({
    selector: 'app-systems-table',
    standalone: true,
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        InputTextGroupComponent,
        SelectionTableComponent,
        SelectionTableFilterDirective,
    ],
    template: `
        <form class="border bg-white" [formGroup]="searchForm">
            <app-selection-table
                [rows]="filteredSystems()"
                [columns]="columns()"
                sortedBy="systemNo"
                [rowLabel]="systemNo"
                [selectAllLabel]="
                    'customizedexports_export_step_systems_select_all'
                        | translate: 'online' : 'export'
                "
                [emptyText]="'table_noResults' | translate: 'online' : 'common'"
                (rowToggled)="systemToggled.emit($event)"
                (rowsSelected)="systemsSelected.emit($event)"
                (rowsDeselected)="systemsDeselected.emit($event)">
                <ng-template appSelectionTableFilter="systemNo">
                    <app-input-text-group
                        formControlName="systemNo"
                        [placeholder]="
                            'customizedexports_export_step_systems_search_system_no'
                                | translate: 'online' : 'export'
                        "></app-input-text-group>
                </ng-template>
                <ng-template appSelectionTableFilter="buildingName">
                    <app-input-text-group
                        formControlName="buildingName"
                        [placeholder]="
                            'customizedexports_export_step_systems_search_building_name'
                                | translate: 'online' : 'export'
                        "></app-input-text-group>
                </ng-template>
                <ng-template appSelectionTableFilter="address">
                    <app-input-text-group
                        formControlName="address"
                        [placeholder]="
                            'customizedexports_export_step_systems_search_address'
                                | translate: 'online' : 'export'
                        "></app-input-text-group>
                </ng-template>
            </app-selection-table>
        </form>

        <div class="d-flex gap-2 mt-3">
            <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                [disabled]="selectedSystemIds().length === 0"
                (click)="cleared.emit()">
                {{
                    'customizedexports_export_step_systems_clear_all'
                        | translate: 'online' : 'export'
                }}
            </button>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemsTableComponent {
    private readonly formBuilder = inject(FormBuilder);

    readonly systems = input.required<ReadonlyArray<SystemRow>>();
    readonly selectedSystemIds = input.required<ReadonlyArray<string>>();

    readonly systemToggled = output<string>();
    readonly systemsSelected = output<string[]>();
    readonly systemsDeselected = output<string[]>();
    readonly cleared = output<void>();

    protected readonly searchForm = this.formBuilder.nonNullable.group({
        systemNo: '',
        buildingName: '',
        address: '',
    });

    private readonly searchValues = toSignal(fromControl(this.searchForm), {
        initialValue: this.searchForm.getRawValue(),
    });

    private readonly showsBuildingNameColumn = computed(() =>
        this.systems().some(system => system.buildingName != null && system.buildingName !== ''),
    );

    protected readonly columns = computed((): SelectionTableColumn<SelectableSystemRow>[] => [
        {
            key: 'systemNo',
            labelKey: 'customizedexports_export_step_systems_col_system_no',
            text: system => system.systemNo,
            compare: direction =>
                numericLocaleComparator<SelectableSystemRow>(system => system.systemNo, direction),
        },
        {
            key: 'buildingName',
            labelKey: 'customizedexports_export_step_systems_col_building_name',
            hidden: !this.showsBuildingNameColumn(),
            text: system => system.buildingName ?? '',
            truncate: true,
            compare: direction =>
                caseInsensitiveComparator<SelectableSystemRow>(
                    system => system.buildingName ?? '',
                    direction,
                ),
        },
        {
            key: 'address',
            labelKey: 'customizedexports_export_step_systems_col_address',
            text: system => system.address,
            truncate: true,
            compare: direction =>
                caseInsensitiveComparator<SelectableSystemRow>(system => system.address, direction),
        },
    ]);

    protected readonly filteredSystems = computed((): SelectableSystemRow[] => {
        const search = this.searchValues();
        const selected = new Set(this.selectedSystemIds());
        // The buildingName control keeps a stale value once its column (and search box) unrenders
        // (Angular does not reset a control's value when it leaves the form), so the predicate must
        // only apply it while the column is actually visible.
        const buildingNameColumnVisible = this.showsBuildingNameColumn();
        return this.systems()
            .filter(
                system =>
                    includesIgnoreCase(system.systemNo, search.systemNo) &&
                    (!buildingNameColumnVisible ||
                        includesIgnoreCase(system.buildingName, search.buildingName)) &&
                    includesIgnoreCase(system.address, search.address),
            )
            .map(system => ({
                ...system,
                id: system.systemNo,
                selected: selected.has(system.systemNo),
            }));
    });

    protected readonly systemNo = (system: SelectableSystemRow): string => system.systemNo;
}
